import { useEffect, useRef, type ReactNode } from "react";
import type { ProviderVerificationDto, ProviderView } from "../lib/api";
import { healthDisplay, type HealthTone } from "../lib/provider-display";

/* ── Page structure ────────────────────────────────────────────── */

export function PageHeader({
  title,
  subtitle,
  right,
}: {
  title: string;
  subtitle?: string;
  right?: ReactNode;
}) {
  return (
    <header className="mb-5 flex flex-wrap items-start justify-between gap-3">
      <div>
        <h1 className="text-xl font-semibold tracking-tight">{title}</h1>
        {subtitle && <p className="mt-0.5 text-sm text-canvas-muted">{subtitle}</p>}
      </div>
      {right && <div className="flex items-center gap-2">{right}</div>}
    </header>
  );
}

export function SectionHeader({
  title,
  subtitle,
  right,
}: {
  title: string;
  subtitle?: string;
  right?: ReactNode;
}) {
  return (
    <div className="mb-3 flex flex-wrap items-end justify-between gap-2">
      <div>
        <h2 className="text-sm font-semibold tracking-tight">{title}</h2>
        {subtitle && <p className="mt-0.5 text-xs text-canvas-muted">{subtitle}</p>}
      </div>
      {right}
    </div>
  );
}

/* ── Metrics ───────────────────────────────────────────────────── */

export function Metric({
  label,
  value,
  hint,
  tone,
}: {
  label: string;
  value: string | number;
  hint?: string;
  tone?: "ok" | "warn" | "danger";
}) {
  const valueCls =
    tone === "danger"
      ? "text-danger"
      : tone === "warn"
        ? "text-warn"
        : tone === "ok"
          ? "text-accent"
          : "";
  return (
    <div className="panel px-4 py-3">
      <div className="metric-label">{label}</div>
      <div className={`metric-value mt-0.5 ${valueCls}`}>{value}</div>
      {hint && <div className="mt-0.5 truncate text-[11px] text-canvas-muted">{hint}</div>}
    </div>
  );
}

/* ── Status ────────────────────────────────────────────────────── */

const TONE_DOT: Record<HealthTone, string> = {
  ok: "bg-accent",
  warn: "bg-warn",
  danger: "bg-danger",
  muted: "bg-canvas-muted",
};
const TONE_TEXT: Record<HealthTone, string> = {
  ok: "text-accent",
  warn: "text-warn",
  danger: "text-danger",
  muted: "text-canvas-muted",
};

export function Dot({ tone, pulse }: { tone: HealthTone; pulse?: boolean }) {
  return (
    <span
      className={`inline-block h-1.5 w-1.5 rounded-full ${TONE_DOT[tone]} ${
        tone === "ok" ? "shadow-[0_0_6px_rgba(61,220,151,0.6)]" : ""
      } ${pulse ? "animate-pulse" : ""}`}
    />
  );
}

export function StatusDot({
  status,
  showLabel = true,
}: {
  status: ProviderView["connectionStatus"];
  showLabel?: boolean;
}) {
  const h = healthDisplay(status);
  return (
    <span className={`inline-flex items-center gap-1.5 text-xs font-medium ${TONE_TEXT[h.tone]}`}>
      <Dot tone={h.tone} />
      {showLabel && h.label}
    </span>
  );
}

/** Generic status pill for health/request/key statuses. */
export function StatusBadge({ label, tone }: { label: string; tone?: HealthTone }) {
  const t = tone ?? "muted";
  const cls =
    t === "ok"
      ? "chip-ok"
      : t === "warn"
        ? "chip-warn"
        : t === "danger"
          ? "chip-danger"
          : "chip-muted";
  return <span className={cls}>{label}</span>;
}

export function toneForStatus(status: string): HealthTone {
  if (["healthy", "connected", "success", "active", "ok"].includes(status)) return "ok";
  if (["degraded", "verifying", "connecting", "warn"].includes(status)) return "warn";
  if (["unhealthy", "quota_exhausted", "error", "revoked"].includes(status))
    return "danger";
  return "muted";
}

/* ── Branding ──────────────────────────────────────────────────── */

export function ProviderMark({
  letter,
  accent,
  size = 34,
}: {
  letter: string;
  accent: string;
  size?: number;
}) {
  return (
    <div
      className="flex shrink-0 items-center justify-center rounded-lg font-semibold text-white"
      style={{
        width: size,
        height: size,
        background: `linear-gradient(135deg, ${accent}, ${accent}cc)`,
        fontSize: size * 0.38,
        boxShadow: `0 0 0 1px ${accent}33`,
      }}
      aria-hidden
    >
      {letter}
    </div>
  );
}

/* ── States ────────────────────────────────────────────────────── */

export function EmptyState({
  title,
  body,
  action,
}: {
  title: string;
  body: string;
  action?: ReactNode;
}) {
  return (
    <div className="panel flex flex-col items-center justify-center px-6 py-10 text-center">
      <p className="mb-1 text-sm font-medium">{title}</p>
      <p className="max-w-md text-sm text-canvas-muted">{body}</p>
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}

export function ErrorState({
  message,
  onRetry,
}: {
  message: string;
  onRetry?: () => void;
}) {
  return (
    <div className="panel flex items-center justify-between gap-3 border-danger/40 bg-danger/5 px-4 py-3">
      <p className="text-sm text-danger">{message}</p>
      {onRetry && (
        <button className="btn-ghost shrink-0 !py-1 text-xs" onClick={onRetry}>
          Retry
        </button>
      )}
    </div>
  );
}

export function SkeletonRows({ rows = 4, height = 62 }: { rows?: number; height?: number }) {
  return (
    <div className="animate-pulse space-y-2">
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="panel" style={{ height }} />
      ))}
    </div>
  );
}

/* ── Filter bar ────────────────────────────────────────────────── */

export interface FilterPill {
  id: string;
  label: string;
  count?: number | null;
}

export function FilterBar({
  query,
  onQuery,
  placeholder,
  pills,
  activePill,
  onPill,
  sort,
  onSort,
  sortOptions,
  right,
}: {
  query?: string;
  onQuery?: (q: string) => void;
  placeholder?: string;
  pills?: FilterPill[];
  activePill?: string;
  onPill?: (id: string) => void;
  sort?: string;
  onSort?: (s: string) => void;
  sortOptions?: Array<{ id: string; label: string }>;
  right?: ReactNode;
}) {
  return (
    <div className="mb-5 flex flex-wrap items-center gap-2">
      {onQuery && (
        <input
          value={query}
          onChange={(e) => onQuery(e.target.value)}
          placeholder={placeholder ?? "Search…"}
          className="w-full flex-1 rounded-lg border border-canvas-border bg-canvas-raised px-3 py-2 text-sm outline-none focus:border-accent sm:min-w-64 sm:max-w-xs"
        />
      )}
      {pills && (
        <div className="flex flex-wrap items-center gap-1">
          {pills.map((f) => (
            <button
              key={f.id}
              onClick={() => onPill?.(f.id)}
              className={`rounded-full px-3 py-1 text-xs font-medium transition ${
                activePill === f.id
                  ? "bg-accent/15 text-accent"
                  : "text-canvas-muted hover:bg-canvas-raised hover:text-canvas-ink"
              }`}
            >
              {f.label}
              {f.count != null && <span className="ml-1 opacity-70">{f.count}</span>}
            </button>
          ))}
        </div>
      )}
      {sortOptions && onSort && (
        <select
          value={sort}
          onChange={(e) => onSort(e.target.value)}
          className="rounded-lg border border-canvas-border bg-canvas-raised px-2.5 py-1.5 text-xs text-canvas-muted outline-none focus:border-accent"
          aria-label="Sort"
        >
          {sortOptions.map((s) => (
            <option key={s.id} value={s.id}>
              {s.label}
            </option>
          ))}
        </select>
      )}
      {right}
    </div>
  );
}

/* ── Drawer / Modal ────────────────────────────────────────────── */

export function Drawer({
  title,
  onClose,
  children,
  wide,
}: {
  title: ReactNode;
  onClose: () => void;
  children: ReactNode;
  wide?: boolean;
}) {
  const ref = useRef<HTMLElement>(null);
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    window.addEventListener("keydown", onKey);
    ref.current?.querySelector<HTMLElement>("button")?.focus();
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-40 flex justify-end" role="dialog" aria-modal="true">
      <button
        className="absolute inset-0 bg-black/60 backdrop-blur-[2px]"
        aria-label="Close"
        onClick={onClose}
      />
      <aside
        ref={ref}
        className={`relative flex h-full w-full flex-col border-l border-canvas-border bg-canvas-raised shadow-2xl ${
          wide ? "max-w-xl" : "max-w-md"
        }`}
      >
        <div className="flex items-center justify-between border-b border-canvas-border px-5 py-4">
          <h2 className="min-w-0 text-sm font-semibold">{title}</h2>
          <button className="btn-ghost shrink-0 !px-2.5 !py-1 text-xs" onClick={onClose}>
            Close
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-5">{children}</div>
      </aside>
    </div>
  );
}

export function DrawerSection({
  title,
  children,
  right,
}: {
  title: string;
  children: ReactNode;
  right?: ReactNode;
}) {
  return (
    <section className="mb-5">
      <div className="mb-2 flex items-center justify-between">
        <h4 className="text-[11px] font-semibold uppercase tracking-wider text-canvas-muted">
          {title}
        </h4>
        {right}
      </div>
      {children}
    </section>
  );
}

export function Modal({
  title,
  onClose,
  children,
  wide,
}: {
  title: ReactNode;
  onClose: () => void;
  children: ReactNode;
  wide?: boolean;
}) {
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center p-4" role="dialog" aria-modal="true">
      <button
        className="absolute inset-0 bg-black/60 backdrop-blur-[2px]"
        aria-label="Close"
        onClick={onClose}
      />
      <div
        className={`panel relative flex max-h-[85vh] w-full flex-col overflow-hidden ${
          wide ? "max-w-3xl" : "max-w-lg"
        }`}
      >
        <div className="flex items-center justify-between border-b border-canvas-border px-5 py-4">
          <h2 className="text-sm font-semibold">{title}</h2>
          <button className="btn-ghost !px-2.5 !py-1 text-xs" onClick={onClose}>
            Close
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-5">{children}</div>
      </div>
    </div>
  );
}

/* ── Timeline / trace ──────────────────────────────────────────── */

export function TraceStep({
  index,
  title,
  detail,
  tone = "muted",
  last,
}: {
  index: number;
  title: string;
  detail?: ReactNode;
  tone?: HealthTone;
  last?: boolean;
}) {
  return (
    <li className="relative flex gap-3 pb-4">
      {!last && (
        <span className="absolute left-[9px] top-5 h-full w-px bg-canvas-border" aria-hidden />
      )}
      <span
        className={`z-10 flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[10px] font-semibold ${
          tone === "ok"
            ? "bg-accent/20 text-accent"
            : tone === "danger"
              ? "bg-danger/20 text-danger"
              : tone === "warn"
                ? "bg-warn/20 text-warn"
                : "bg-canvas-raised text-canvas-muted"
        }`}
      >
        {tone === "ok" ? "✓" : tone === "danger" ? "✕" : index}
      </span>
      <div className="min-w-0 flex-1">
        <div className="text-sm font-medium">{title}</div>
        {detail && <div className="mt-0.5 text-xs text-canvas-muted">{detail}</div>}
      </div>
    </li>
  );
}

/* ── Charts (dependency-free) ──────────────────────────────────── */

export function Bars({
  data,
  height = 96,
  format,
}: {
  data: Array<{ label: string; value: number; title?: string; tone?: HealthTone }>;
  height?: number;
  format?: (v: number) => string;
}) {
  const max = Math.max(1, ...data.map((d) => d.value));
  if (data.length === 0) return null;
  return (
    <div>
      <div className="flex items-end gap-[3px]" style={{ height }}>
        {data.map((d, i) => (
          <div
            key={i}
            className={`group relative min-w-[3px] flex-1 rounded-t transition ${
              d.tone === "danger"
                ? "bg-danger/70 hover:bg-danger"
                : d.tone === "warn"
                  ? "bg-warn/70 hover:bg-warn"
                  : "bg-accent/60 hover:bg-accent"
            }`}
            style={{ height: `${Math.max(3, (d.value / max) * 100)}%` }}
            title={d.title ?? `${d.label}: ${format ? format(d.value) : d.value}`}
          />
        ))}
      </div>
      <div className="mt-1 flex justify-between text-[10px] text-canvas-muted">
        <span>{data[0]?.label}</span>
        <span>{data[data.length - 1]?.label}</span>
      </div>
    </div>
  );
}

export function Sparkline({
  data,
  width = 120,
  height = 28,
}: {
  data: number[];
  width?: number;
  height?: number;
}) {
  if (data.length < 2) return null;
  const max = Math.max(...data);
  const min = Math.min(...data);
  const range = max - min || 1;
  const pts = data
    .map(
      (v, i) =>
        `${(i / (data.length - 1)) * width},${height - 2 - ((v - min) / range) * (height - 4)}`,
    )
    .join(" ");
  return (
    <svg width={width} height={height} className="overflow-visible" aria-hidden>
      <polyline
        points={pts}
        fill="none"
        stroke="var(--color-accent, #3DDC97)"
        strokeWidth="1.5"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/** 0–1 score rendered as a labeled mini bar. */
export function ScoreBar({ label, value }: { label: string; value: number }) {
  const pct = Math.round(value * 100);
  return (
    <div className="flex items-center gap-2">
      <span className="w-20 text-[11px] text-canvas-muted">{label}</span>
      <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-canvas">
        <div
          className={`h-full rounded-full ${value >= 0.7 ? "bg-accent" : value >= 0.4 ? "bg-warn" : "bg-danger"}`}
          style={{ width: `${Math.max(3, pct)}%` }}
        />
      </div>
      <span className="mono w-10 text-right text-[11px] text-canvas-muted">
        {value.toFixed(2)}
      </span>
    </div>
  );
}

export function Progress({ ratio }: { ratio: number | null }) {
  if (ratio == null) return <span className="text-xs text-canvas-muted">unknown</span>;
  const pct = Math.round(ratio * 100);
  return (
    <div className="flex items-center gap-2">
      <div className="h-1.5 w-16 overflow-hidden rounded-full bg-canvas">
        <div
          className={`h-full rounded-full ${ratio > 0.3 ? "bg-accent" : ratio > 0.1 ? "bg-warn" : "bg-danger"}`}
          style={{ width: `${Math.max(2, pct)}%` }}
        />
      </div>
      <span className="mono text-[11px] text-canvas-muted">{pct}%</span>
    </div>
  );
}

export function CopyButton({ text, label = "Copy" }: { text: string; label?: string }) {
  return (
    <button
      className="btn-ghost !px-2.5 !py-1 text-xs"
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(text);
        } catch {
          /* clipboard unavailable */
        }
      }}
    >
      {label}
    </button>
  );
}

export function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="mb-3">
      <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wider text-canvas-muted">
        {label}
      </label>
      {children}
    </div>
  );
}

/* ── Verification checklist (shared by connect + detail drawers) ── */

function sanitizeStepDetail(detail: string | undefined): string {
  if (!detail) return "";
  const d = detail.trim();
  if (d.includes("<") || d.length > 140) {
    if (/^<!doctype|^<html/i.test(d)) return "Provider returned an HTML error page";
    return `${d.slice(0, 140)}…`;
  }
  return d;
}

export function VerificationChecklist({
  verification,
}: {
  verification: ProviderVerificationDto;
}) {
  const rawDetails = verification.steps
    .filter((s) => s.detail && (s.detail.includes("<") || s.detail.length > 140))
    .map((s) => ({ step: s.label, detail: s.detail! }));
  return (
    <div className="mb-4 rounded-xl border border-canvas-border bg-canvas p-3.5">
      <div className="mb-2.5 flex items-center justify-between">
        <span className={`text-sm font-semibold ${verification.ok ? "text-accent" : "text-warn"}`}>
          {verification.ok ? "Verified" : "Verification incomplete"}
        </span>
        {verification.modelsDiscovered != null && verification.ok && (
          <span className="chip-ok">{verification.modelsDiscovered} models</span>
        )}
      </div>
      <ul className="space-y-1.5 text-sm">
        {verification.steps.map((s) => (
          <li key={s.id} className="flex gap-2">
            <span className={s.ok ? "text-accent" : "text-danger"}>
              {s.ok ? "✓" : "✕"}
            </span>
            <span>
              {s.label}
              {s.detail ? (
                <span className="ml-1.5 text-xs text-canvas-muted">
                  — {sanitizeStepDetail(s.detail)}
                </span>
              ) : null}
            </span>
          </li>
        ))}
      </ul>
      {verification.error && (
        <div className="mt-3 rounded-lg border border-danger/40 bg-danger/5 px-3 py-2 text-sm">
          <p className="text-danger">{verification.error.message}</p>
          {verification.error.action && (
            <p className="mt-1 text-xs text-canvas-muted">{verification.error.action}</p>
          )}
        </div>
      )}
      {(rawDetails.length > 0 || verification.error?.code) && (
        <details className="mt-3">
          <summary className="cursor-pointer text-[11px] font-medium uppercase tracking-wider text-canvas-muted hover:text-canvas-ink">
            Advanced details
          </summary>
          <div className="mt-2 space-y-1.5 rounded-lg border border-canvas-border bg-canvas-raised p-2.5 text-[11px]">
            {verification.error?.code && (
              <p className="mono text-canvas-muted">code: {verification.error.code}</p>
            )}
            {verification.verifiedAt && (
              <p className="mono text-canvas-muted">verified: {verification.verifiedAt}</p>
            )}
            {rawDetails.map((r) => (
              <p key={r.step} className="mono break-all text-canvas-muted">
                {r.step}: {r.detail.slice(0, 500)}
              </p>
            ))}
          </div>
        </details>
      )}
    </div>
  );
}
